import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  User,
  Plus,
  Search,
  CheckCircle2,
  XCircle,
  Shield,
  ShieldCheck,
  Trash2,
  ChevronDown,
  ChevronUp,
  UserPlus,
  Phone,
  Layers,
  AlertCircle,
  Power,
  FolderPlus,
  FileText,
} from "lucide-react";
import TspApiService from "../../api/tspApiService";
import useTspPermission from "../../hooks/useTspPermission";
import TSP_PERMISSIONS from "../../constants/tspPermissions";
import { TspBreadcrumb, TspCard, TspEmptyState, TspSkeleton, TspStatCard } from "../../components/primitives";
import { initialsOf } from "../../utils/tspFormat";
import TspBackButton from "../../components/common/TspBackButton";

export default function TspTeamsPage() {
  const { hasPermission } = useTspPermission();
  const canManage = hasPermission(TSP_PERMISSIONS.TEAMS_MANAGE);

  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);

  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamDescription, setNewTeamDescription] = useState("");
  const [creating, setCreating] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const loadTeams = () => {
    setLoading(true);
    TspApiService.listTeams()
      .then((res) => setTeams(res.data || []))
      .catch((err) => setError(err?.response?.data?.message || "Failed to load teams"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTeams();
  }, []);

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      await TspApiService.createTeam({ name: newTeamName.trim(), description: newTeamDescription.trim() });
      setNewTeamName("");
      setNewTeamDescription("");
      loadTeams();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create team");
    } finally {
      setCreating(false);
    }
  };

  const handleToggleStatus = async (team) => {
    const nextStatus = team.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await TspApiService.setTeamStatus(team.id, nextStatus);
      loadTeams();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update team status");
    }
  };

  // Filtered teams list
  const filteredTeams = useMemo(() => {
    return teams.filter((team) => {
      const matchesSearch =
        !searchTerm.trim() ||
        (team.name && team.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (team.description && team.description.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus =
        statusFilter === "ALL" ||
        (statusFilter === "ACTIVE" && team.status === "ACTIVE") ||
        (statusFilter === "INACTIVE" && team.status !== "ACTIVE");

      return matchesSearch && matchesStatus;
    });
  }, [teams, searchTerm, statusFilter]);

  const activeCount = useMemo(() => teams.filter((t) => t.status === "ACTIVE").length, [teams]);
  const inactiveCount = useMemo(() => teams.filter((t) => t.status !== "ACTIVE").length, [teams]);

  return (
    <div className="tsp-page tsp-teams-page">
      {/* Heading & Breadcrumbs */}
      <div className="tsp-page-heading-row">
        <div>
          <h1>TSP Teams Management</h1>
          <TspBreadcrumb items={["Dashboard", "Bus Leaders Directory", "Manage Teams"]} />
        </div>
        <div className="tsp-heading-actions">
          <TspBackButton fallback="/tsp/teams" />
          <Link to="/tsp/teams" className="tsp-btn tsp-btn-outline-primary">
            <Users size={15} aria-hidden="true" /> View Bus Leaders Directory
          </Link>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="tsp-alert tsp-alert-error" role="alert">
          <AlertCircle size={18} aria-hidden="true" />
          <span>{error}</span>
          <button type="button" className="tsp-alert-close" onClick={() => setError(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      {/* Overview Stat Cards */}
      <div className="tsp-stat-cards-row tsp-leaders-stats">
        <TspStatCard icon={Layers} value={teams.length} label="Total Teams" tone="primary" />
        <TspStatCard icon={CheckCircle2} value={activeCount} label="Active Teams" tone="green" />
        <TspStatCard icon={XCircle} value={inactiveCount} label="Inactive Teams" tone="amber" />
        <TspStatCard icon={Users} value={teams.reduce((acc, t) => acc + (t.memberCount || 0), 0)} label="Registered Members" tone="blue" />
      </div>

      {/* Create Team Form Card (For Authorized Managers) */}
      {canManage && (
        <TspCard
          title="Create New Team"
          subtitle="Add a new Travelling Sankirtan Party team to assign bus leaders and members"
          className="tsp-team-form-card"
        >
          <form className="tsp-team-create-form" onSubmit={handleCreateTeam}>
            <div className="tsp-form-row">
              <div className="tsp-form-field">
                <label htmlFor="team-name">
                  Team Name <span className="tsp-required">*</span>
                </label>
                <div className="tsp-input-with-icon">
                  <Users size={16} aria-hidden="true" />
                  <input
                    id="team-name"
                    type="text"
                    className="tsp-input"
                    value={newTeamName}
                    onChange={(e) => setNewTeamName(e.target.value)}
                    placeholder="e.g. North Zone Sankirtan Team"
                    required
                  />
                </div>
              </div>

              <div className="tsp-form-field">
                <label htmlFor="team-desc">Description</label>
                <div className="tsp-input-with-icon">
                  <FileText size={16} aria-hidden="true" />
                  <input
                    id="team-desc"
                    type="text"
                    className="tsp-input"
                    value={newTeamDescription}
                    onChange={(e) => setNewTeamDescription(e.target.value)}
                    placeholder="e.g. Covering Delhi NCR & Rajasthan route"
                  />
                </div>
              </div>

              <div className="tsp-form-actions-inline">
                <button type="submit" className="tsp-btn tsp-btn-primary" disabled={creating}>
                  <Plus size={16} aria-hidden="true" />
                  {creating ? "Creating…" : "Create Team"}
                </button>
              </div>
            </div>
          </form>
        </TspCard>
      )}

      {/* Filter & Search Bar */}
      <div className="tsp-journey-filters" role="search">
        <label className="tsp-journey-search">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            placeholder="Search teams by name or description…"
            aria-label="Search TSP teams"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </label>

        <div className="tsp-chip-filter-row" role="group" aria-label="Filter by status" style={{ margin: 0 }}>
          <button
            type="button"
            className="tsp-chip-filter"
            aria-pressed={statusFilter === "ALL"}
            onClick={() => setStatusFilter("ALL")}
          >
            All <span>{teams.length}</span>
          </button>
          <button
            type="button"
            className="tsp-chip-filter"
            aria-pressed={statusFilter === "ACTIVE"}
            onClick={() => setStatusFilter("ACTIVE")}
          >
            Active <span>{activeCount}</span>
          </button>
          <button
            type="button"
            className="tsp-chip-filter"
            aria-pressed={statusFilter === "INACTIVE"}
            onClick={() => setStatusFilter("INACTIVE")}
          >
            Inactive <span>{inactiveCount}</span>
          </button>
        </div>
      </div>

      {/* Teams Table Card */}
      <TspCard
        title={`All Teams (${filteredTeams.length})`}
        subtitle="Manage team records, members and leadership status"
      >
        {loading ? (
          <TspSkeleton count={4} label="Loading teams" />
        ) : filteredTeams.length === 0 ? (
          <TspEmptyState
            icon={teams.length === 0 ? FolderPlus : Search}
            title={teams.length === 0 ? "No TSP teams yet" : "No teams match your search"}
            message={
              teams.length === 0
                ? "Create your first Travelling Sankirtan team using the form above."
                : "Try adjusting your search terms or filter to find the team."
            }
          />
        ) : (
          <div className="tsp-table-wrap">
            <table className="tsp-table tsp-teams-table">
              <thead>
                <tr>
                  <th style={{ width: "35%" }}>Team Name</th>
                  <th style={{ width: "35%" }}>Description</th>
                  <th style={{ width: "12%" }}>Status</th>
                  <th style={{ width: "18%", textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTeams.map((team) => {
                  const isExpanded = selectedTeamId === team.id;
                  return (
                    <React.Fragment key={team.id}>
                      <tr className={isExpanded ? "tsp-row-expanded" : ""}>
                        <td>
                          <div className="tsp-team-name-cell">
                            <span className="tsp-team-avatar" aria-hidden="true">
                              {initialsOf(team.name || "T")}
                            </span>
                            <div className="tsp-team-info">
                              <span className="tsp-team-title">{team.name}</span>
                              <span className="tsp-team-id-badge">ID #{team.id}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="tsp-team-desc">
                            {team.description ? team.description : <span className="tsp-muted-dash">—</span>}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`tsp-status-pill ${
                              team.status === "ACTIVE" ? "tsp-status-pill-success" : "tsp-status-pill-danger"
                            }`}
                          >
                            {team.status || "ACTIVE"}
                          </span>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <div className="tsp-table-actions-row">
                            <button
                              type="button"
                              className={`tsp-btn tsp-btn-sm ${isExpanded ? "tsp-btn-primary" : "tsp-btn-outline-primary"}`}
                              onClick={() => setSelectedTeamId(isExpanded ? null : team.id)}
                              aria-expanded={isExpanded}
                            >
                              <Users size={13} aria-hidden="true" />
                              {isExpanded ? "Hide Members" : "View Members"}
                              {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                            </button>

                            {canManage && (
                              <button
                                type="button"
                                className={`tsp-btn tsp-btn-sm ${
                                  team.status === "ACTIVE" ? "tsp-btn-outline-danger" : "tsp-btn-outline-success"
                                }`}
                                onClick={() => handleToggleStatus(team)}
                                title={team.status === "ACTIVE" ? "Deactivate team" : "Activate team"}
                              >
                                <Power size={13} aria-hidden="true" />
                                {team.status === "ACTIVE" ? "Deactivate" : "Activate"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="tsp-expanded-content-row">
                          <td colSpan={4}>
                            <TspTeamMembersPanel team={team} canManage={canManage} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </TspCard>
    </div>
  );
}

function TspTeamMembersPanel({ team, canManage }) {
  const teamId = team.id;
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Manual Member Name & Optional Phone input + Devotee User resolver
  const [memberName, setMemberName] = useState("");
  const [memberPhone, setMemberPhone] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUserRole, setNewUserRole] = useState("MEMBER");
  const [adding, setAdding] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const loadMembers = () => {
    setLoading(true);
    TspApiService.listTeamMembers(teamId)
      .then((res) => setMembers(res.data || []))
      .catch((err) => setError(err?.response?.data?.message || "Failed to load members"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  // Live search for matching devotees as user types
  useEffect(() => {
    const query = memberName.trim();
    if (query.length < 2 || (selectedUser && selectedUser.name === query)) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    let active = true;
    const timer = setTimeout(async () => {
      try {
        if (typeof TspApiService.searchTeamUsers === "function") {
          const res = await TspApiService.searchTeamUsers(query);
          if (active && res?.data) {
            setSuggestions(res.data || []);
            setShowSuggestions((res.data || []).length > 0);
          }
        }
      } catch {
        // ignore search error
      }
    }, 200);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [memberName, selectedUser]);

  const handleSelectSuggestion = (user) => {
    setSelectedUser(user);
    setMemberName(user.name || "");
    if (user.phone) setMemberPhone(user.phone);
    setShowSuggestions(false);
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    const trimmedName = memberName.trim();
    if (!trimmedName) return;
    setAdding(true);
    setError(null);

    let resolvedUserId = selectedUser?.id;

    // If name is purely digits (e.g. "42"), treat as userId directly
    if (!resolvedUserId && /^\d+$/.test(trimmedName)) {
      resolvedUserId = Number(trimmedName);
    }

    // Auto-search user by name or phone if not selected from suggestions
    if (!resolvedUserId && typeof TspApiService.searchTeamUsers === "function") {
      try {
        const searchRes = await TspApiService.searchTeamUsers(trimmedName);
        const users = searchRes?.data || [];
        if (users.length > 0) {
          const match = users.find((u) => u.name?.toLowerCase() === trimmedName.toLowerCase()) || users[0];
          resolvedUserId = match.id;
        } else if (memberPhone.trim()) {
          const phoneRes = await TspApiService.searchTeamUsers(memberPhone.trim());
          const phoneUsers = phoneRes?.data || [];
          if (phoneUsers.length > 0) {
            resolvedUserId = phoneUsers[0].id;
          }
        }
      } catch {
        // ignore
      }
    }

    const payload = {
      name: trimmedName,
      phone: memberPhone.trim() || undefined,
      teamRole: newUserRole,
    };
    if (resolvedUserId) {
      payload.userId = Number(resolvedUserId);
    }

    try {
      await TspApiService.addTeamMember(teamId, payload);
      setMemberName("");
      setMemberPhone("");
      setSelectedUser(null);
      setNewUserRole("MEMBER");
      setShowSuggestions(false);
      loadMembers();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to add member");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (userId) => {
    setError(null);
    try {
      await TspApiService.removeTeamMember(teamId, userId);
      loadMembers();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to remove member");
    }
  };

  const handleAssignLeader = async (userId) => {
    setError(null);
    try {
      await TspApiService.assignTeamLeader(teamId, userId);
      loadMembers();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to assign leader");
    }
  };

  return (
    <div className="tsp-team-members-drawer">
      <div className="tsp-drawer-header">
        <div className="tsp-drawer-title-group">
          <Users size={17} className="tsp-drawer-icon" aria-hidden="true" />
          <h4 className="tsp-drawer-title">Team Members & Leadership</h4>
          <span className="tsp-drawer-team-pill">{team.name}</span>
          <span className="tsp-drawer-count">({members.length} {members.length === 1 ? "member" : "members"})</span>
        </div>
      </div>

      {error && (
        <div className="tsp-alert tsp-alert-error tsp-alert-sm" role="alert">
          <AlertCircle size={15} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <TspSkeleton count={2} label="Loading team members" />
      ) : members.length === 0 ? (
        <div className="tsp-drawer-empty">
          <Users size={28} className="tsp-muted-text" />
          <p>No members currently assigned to {team.name}.</p>
          <span className="tsp-muted-text">Add team members manually using the form below.</span>
        </div>
      ) : (
        <div className="tsp-members-table-wrap">
          <table className="tsp-table tsp-members-table">
            <thead>
              <tr>
                <th>User / Devotee</th>
                <th>Phone</th>
                <th>Role</th>
                {canManage && <th style={{ textAlign: "right" }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const isLeader = m.team_role === "LEADER";
                const memberId = m.id || m.user_id;
                const displayName = m.user?.name || m.name || (m.user_id ? `User #${m.user_id}` : "Member");
                const displayPhone = m.user?.phone || m.phone || "—";
                return (
                  <tr key={memberId}>
                    <td>
                      <div className="tsp-member-cell">
                        <span className={`tsp-member-avatar ${isLeader ? "is-leader" : ""}`} aria-hidden="true">
                          {initialsOf(displayName)}
                        </span>
                        <div className="tsp-member-info">
                          <span className="tsp-member-name">{displayName}</span>
                          {m.user_id && <span className="tsp-member-sub">ID #{m.user_id}</span>}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="tsp-member-phone">
                        {displayPhone !== "—" ? (
                          <>
                            <Phone size={12} aria-hidden="true" />
                            {displayPhone}
                          </>
                        ) : (
                          "—"
                        )}
                      </span>
                    </td>
                    <td>
                      <span className={`tsp-role-badge ${isLeader ? "tsp-role-leader" : "tsp-role-member"}`}>
                        {isLeader ? (
                          <>
                            <ShieldCheck size={12} aria-hidden="true" /> Leader
                          </>
                        ) : (
                          <>
                            <Shield size={12} aria-hidden="true" /> Member
                          </>
                        )}
                      </span>
                    </td>
                    {canManage && (
                      <td style={{ textAlign: "right" }}>
                        <div className="tsp-table-actions-row">
                          {!isLeader && (
                            <button
                              type="button"
                              className="tsp-btn tsp-btn-sm tsp-btn-outline-primary"
                              onClick={() => handleAssignLeader(memberId)}
                              title="Promote to team leader"
                            >
                              <ShieldCheck size={13} aria-hidden="true" /> Make Leader
                            </button>
                          )}
                          <button
                            type="button"
                            className="tsp-btn tsp-btn-sm tsp-btn-outline-danger"
                            onClick={() => handleRemove(memberId)}
                            title="Remove member from team"
                          >
                            <Trash2 size={13} aria-hidden="true" /> Remove
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Member Form Drawer Footer */}
      {canManage && (
        <div className="tsp-add-member-section">
          <h5 className="tsp-add-member-title">
            <UserPlus size={15} aria-hidden="true" /> Add Member to {team.name}
          </h5>
          <form className="tsp-add-member-form" onSubmit={handleAddMember}>
            <div className="tsp-add-member-grid">
              <div className="tsp-form-field tsp-name-field-wrap">
                <label htmlFor={`member-name-${teamId}`}>
                  Member Name <span className="tsp-required">*</span>
                </label>
                <div className="tsp-input-with-icon">
                  <User size={15} aria-hidden="true" />
                  <input
                    id={`member-name-${teamId}`}
                    type="text"
                    className="tsp-input"
                    value={memberName}
                    onChange={(e) => {
                      setMemberName(e.target.value);
                      if (selectedUser && selectedUser.name !== e.target.value) {
                        setSelectedUser(null);
                      }
                    }}
                    onFocus={() => {
                      if (suggestions.length > 0) setShowSuggestions(true);
                    }}
                    placeholder="e.g. Rahul Sharma"
                    autoComplete="off"
                    required
                  />
                </div>

                {selectedUser && (
                  <span className="tsp-user-id-chip">
                    <CheckCircle2 size={11} /> ID #{selectedUser.id}
                  </span>
                )}

                {showSuggestions && suggestions.length > 0 && (
                  <div className="tsp-devotee-suggestions-dropdown">
                    <div className="tsp-suggestions-header">Matching Registered Devotees</div>
                    {suggestions.map((user) => (
                      <div
                        key={user.id}
                        className="tsp-suggestion-item"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectSuggestion(user);
                        }}
                      >
                        <div className="tsp-suggestion-main">
                          <span className="tsp-suggestion-name">{user.name}</span>
                          {user.phone && <span className="tsp-suggestion-sub">{user.phone}</span>}
                        </div>
                        <span className="tsp-suggestion-id">ID #{user.id}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="tsp-form-field">
                <label htmlFor={`member-phone-${teamId}`}>Phone Number (Optional)</label>
                <div className="tsp-input-with-icon">
                  <Phone size={15} aria-hidden="true" />
                  <input
                    id={`member-phone-${teamId}`}
                    type="tel"
                    className="tsp-input"
                    value={memberPhone}
                    onChange={(e) => setMemberPhone(e.target.value)}
                    placeholder="e.g. +91 98765 43210"
                  />
                </div>
              </div>

              <div className="tsp-form-field">
                <label htmlFor={`user-role-${teamId}`}>Team Role</label>
                <select
                  id={`user-role-${teamId}`}
                  className="tsp-select"
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value)}
                >
                  <option value="MEMBER">Member</option>
                  <option value="LEADER">Leader</option>
                </select>
              </div>

              <div className="tsp-form-actions-inline">
                <button type="submit" className="tsp-btn tsp-btn-primary" disabled={adding || !memberName.trim()}>
                  <UserPlus size={15} aria-hidden="true" />
                  {adding ? "Adding…" : "Add Member"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}


